<template>
  <section class="p-box">
    <header class="p-box__header">
      <h3 class="p-box__title">{{ title }}</h3>
    </header>
    <div class="p-box__body">
      <Info>
        {{ description }}
        <router-link v-if="showOnlyOwnerAuthority" :to="{ name: 'CompaniesManagementKeywords', params: { company_uuid: currentCompanyUuid } }">
          {{ $t('pages.companies.analysis.keywords.keywordManagementLink') }}
        </router-link>
      </Info>
      <div class="p-table">
        <div v-if="items.length !== 0" class="p-table__content">
          <table>
            <thead>
              <tr>
                <th />
                <th class="c-grid__item c-grid__item__s__3 c-grid__item__m__3 c-grid__item__l__3"><span>{{ $t('pages.companies.analysis.keywords.labels.keyword') }}</span></th>
                <th class="c-grid__item c-grid__item__s__3 c-grid__item__m__3 c-grid__item__l__3">
                  <button
                    class="p-table__content__sort"
                    :class="orderCss('positive_count')"
                    @click="sortTables('positive_count')"
                  >
                    {{ $t('pages.companies.analysis.keywords.labels.rate') }}
                  </button>
                </th>
                <th class="c-grid__item c-grid__item__s__1 c-grid__item__m__1 c-grid__item__l__1">
                  <button
                    class="p-table__content__sort"
                    :class="orderCss('total_count')"
                    @click="sortTables('total_count')"
                  >
                    {{ $t('pages.companies.analysis.keywords.labels.count') }}
                  </button>
                </th>
                <th>{{ $t('pages.companies.analysis.keywords.labels.types.title') }}</th>
                <th />
              </tr>
              <tr class="p-table__content__space"><td colspan="6" /></tr>
            </thead>
            <tbody v-if="items && items.length != 0 ">
              <tr v-for="(item, index) in itemList" :key="item.id" class="item">
                <td>{{ `${index + 1}` }}</td>
                <td>
                  {{ item.id }}
                  <span v-if="type == 'specifyKeyword'" class="p-table__content__label">{{ $t('models.keyword.analysis.settings.keyword.rule') }}</span>
                </td>
                <td>
                  <div class="rate">
                    <div class="rate__graph">
                      <ChartHorizontalBar
                        :chart-data="dataForChartHorizontalBar(item)" :options="optionsForChartHorizontalBar" :height="110"
                      />
                    </div>
                    <div>
                      <p class="rate__positive"><span class="rate__positive__circle" />{{ $t('pages.companies.analysis.keywords.positiveRate', { rate: positiveRate(item) }) }}</p>
                      <p class="rate__negative"><span class="rate__negative__circle" />{{ $t('pages.companies.analysis.keywords.negativeRate', { rate: negativeRate(item) }) }}</p>
                    </div>
                  </div>
                </td>
                <td class="u-taR">{{ Number(item.total_count).toLocaleString() }}</td>
                <td class="u-taL">
                  <div class="searchQueryLinks">
                    <div>
                      <component
                        :is="(item.types.find(v => v.type === 'review').count > 0) ? 'router-link' : 'span'"
                        :to="{ name: isCompetitorKeyword ? 'CompaniesCompetitorsReviews' : 'CompaniesReviews', params: { company_uuid: currentCompanyUuid }, query: { query: item.id, ...formValues }}"
                      >
                        {{ $t('pages.companies.analysis.keywords.labels.types.review', { count: item.types.find(v => v.type === 'review').count.toLocaleString() }) }}
                      </component>
                    </div>
                    <div v-if="!isCompetitorKeyword && item.types.find(v => v.type === 'custom_survey_store_answer')">
                      <CustomSurveyLink
                        :current-company-uuid="currentCompanyUuid"
                        :keyword="item.id"
                        :custom-surveys="extractCustomSurveys(item)"
                        :form-values="formValues"
                      />
                    </div>
                    <div v-if="!isCompetitorKeyword">
                      <component
                        :is="(item.types.find(v => v.type === 'complaint').count > 0) ? 'router-link' : 'span'"
                        :to="{ name: 'CompaniesTestimonialIndex', params: { company_uuid: currentCompanyUuid }, query: { query: item.id, text_type: 'complaint', ...formValues }}"
                      >
                        {{ $t('pages.companies.analysis.keywords.labels.types.complaint', { count: item.types.find(v => v.type === 'complaint').count.toLocaleString() }) }}
                      </component>
                    </div>
                  </div>
                </td>
                <td>
                  <div v-if="type == 'keyword'" class="p-table__content__add-ignore" @click="addIgnore(item)">
                    <img :src="iconCrossPath" alt="">
                    {{ $t('pages.companies.analysis.keywords.addIgnore') }}
                  </div>
                  <div v-else />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <template v-else>
          {{ $t('messages.noData') }}
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import localeStringMixin from '../../../mixins/localeStringMixin'
import ChartHorizontalBar from '../../ChartHorizontalBar.vue'
import Info from '../../Info'
import iconCrossPath from '../../../../../images/common/icon_cross.svg'
import CustomSurveyLink from './CustomSurveyLink'

export default {
  name: 'CompaniesAnalysisKeywordsList',
  components: {
    ChartHorizontalBar,
    Info,
    CustomSurveyLink
  },
  mixins: [localeStringMixin],
  props: {
    items: {
      type: Array,
      required: true,
    },
    isCompetitorKeyword: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    formValues: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      sortKey: 'total_count',
      sortOrder: 'desc',
      iconCrossPath,
    }
  },
  computed: {
    ...mapGetters({
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
    }),
    currentCompanyUuid() {
      return this.$route.params.company_uuid
    },
    optionsForChartHorizontalBar() {
      return {
        responsive: true,
        scales: {
          x: {
            display: true,
            stacked: true,
            position: 'top',
            gridLines: {
              drawBorder: false,
              tickMarkLength: 0
            },
            max: 100,
            min: 0,
            ticks: {
              stepSize: 50,
              maxTicksLimit: 3,
            }
          },
          y: {
            display: false,
            stacked: true
          },
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          }
        },
      }
    },
    itemList() {
      let items = this.items.slice(0)
      switch (this.sortOrder) {
      case 'asc':
        items = items.sort((a, b) => {
          if (this.extractItemData(a) < this.extractItemData(b)) return -1
          if (this.extractItemData(a) > this.extractItemData(b)) return 1
          return 0
        })
        break
      case 'desc':
        items = items.sort((a, b) => {
          if (this.extractItemData(a) > this.extractItemData(b)) return -1
          if (this.extractItemData(a) < this.extractItemData(b)) return 1
          return 0
        })
        break
      }

      return items
    }
  },
  methods: {
    ...mapActions({
      createIgnore: 'companiesAnalysisKeywords/createIgnore',
    }),
    dataForChartHorizontalBar(item) {
      if(item.positive_count === 0 && item.negative_count === 0){
        return {
          labels: [''],
          datasets: [
            {
              data: [100],
              backgroundColor: '#d2d5db',
            }
          ]
        }
      }
      return {
        labels: [''],
        datasets: [
          {
            data: [this.positiveRate(item)],
            backgroundColor: '#4C959A',
          },
          {
            data: [this.negativeRate(item)],
            backgroundColor: '#E9446A',
          }
        ]
      }
    },
    total(item) {
      return item.positive_count + item.negative_count
    },
    positiveRate(item) {
      return this.total(item) == 0 ? 0 : Math.ceil((item.positive_count / this.total(item)) * 100)
    },
    negativeRate(item) {
      return this.total(item) == 0 ? 0 : Math.floor((item.negative_count / this.total(item)) * 100)
    },
    sortTables(sortKey) {
      if (this.sortKey === sortKey) {
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortKey = sortKey
        this.sortOrder = 'desc'
      }
    },
    orderCss(key) {
      if (!this.sortOrder) return ''
      if (this.sortKey === key) {
        return this.sortOrder === 'asc' ? 'is-asc' : 'is-desc'
      } else {
        return ''
      }
    },
    extractItemData(item) {
      if (this.sortKey  === 'positive_count') {
        return this.positiveRate(item)
      }
      return item[this.sortKey]
    },
    addIgnore(item) {
      const payload = {
        rule: item.id,
      }
      this.createIgnore({ company_uuid: this.currentCompanyUuid, payload: payload })
    },
    extractCustomSurveys(item) {
      const customSurveyStoreAnswer = item.types.find(v => v.type === 'custom_survey_store_answer')

      if(customSurveyStoreAnswer){
        return customSurveyStoreAnswer.custom_surveys
      }
      return []
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/_var.scss';

.p-table__content {
  td,td:last-child {
    border: none;
    border-bottom: 1px solid #E4E7EB;
  }

  &__label {
    display: inline-flex;
    align-items: center;
    height: 20px;
    margin-left: 8px;
    padding: 4px 6px;
    border-radius: 10px;
    background-color: #3a3b3f;
    color: #fff;
    font-weight: 700;
    font-size: 10px;
    line-height: 1;
  }

  &__add-ignore {
    display: flex;
    margin-top: 5px;
    cursor: pointer;
    img {
      margin-right: 10px;
    }
  }

  .searchQueryLinks {
    font-size: 12px;
    line-height: 2;
  }
}

.rate {
  align-items: center;
  font-size: 12px;
  &__graph {
    width: 90%;
  }

  &__positive {
    &__circle {
      display: inline-block;
      width: 10px;
      height: 10px;
      margin-right: 5px;
      background: $complete-color;
      border-radius: 100%;
    }
  }

  &__negative {
    &__circle {
      display: inline-block;
      width: 10px;
      height: 10px;
      margin-right: 5px;
      background: #E9446A;
      border-radius: 100%;
    }
  }
}

.p-box:not(:first-child) {
  margin-top: 48px;
}
</style>
